import {
  BaseDataService,
  CompanyService,
  type BaseDataTreeNodeDto,
  type GroupId,
  type ConstructionModelFilter,
  type CompanyTextsDto,
  type ElementDto,
} from '@swpsoftware/commons-api/hey-mengencloud';
import type { CompanyTextTreeItem } from '@/models/CompanyTextTreeItem';
import { nodesToItems, treeTransform } from '@/transformers/GenericTreeTransform';
import { defineStore } from 'pinia';

export type TextSource = 'company' | 'standard';

interface ConstructionModelCount {
  common: number;
  wood: number;
  solid: number;
}

const findParentWithChanges = (companyTextTree: CompanyTextTreeItem[], changedIds: Set<string>): Set<string> => {
  let resultSet: Set<string> = new Set();
  for (const item of companyTextTree) {
    if (changedIds.has(item.id)) {
      resultSet.add(item.id);
    }
    if (item.items !== undefined) {
      const changedChildIds: Set<string> = findParentWithChanges(item.items, changedIds);
      if (changedChildIds.size > 0) {
        resultSet.add(item.id);
        resultSet = new Set([...resultSet, ...changedChildIds]);
      }
    }
  }
  return resultSet;
};

export const useTemplateBaseDataStore = defineStore({
  id: 'TemplateBaseData',
  state: (): {
    elements: Record<string, ElementDto>;
    companyTexts: CompanyTextsDto;
    baseDataTree: BaseDataTreeNodeDto[];
    companyTextTree: CompanyTextTreeItem[];
  } => ({
    elements: {},
    companyTexts: { descriptions: {}, titles: {} },
    baseDataTree: [],
    companyTextTree: [],
  }),

  getters: {
    changedTextIds: (state) =>
      new Set([...Object.keys(state.companyTexts.titles), ...Object.keys(state.companyTexts.descriptions)]),
    changedParentIds(state): Set<string> {
      return findParentWithChanges(state.companyTextTree, this.changedTextIds);
    },
  },

  actions: {
    async init() {
      await Promise.all([this.loadElements(), this.loadBaseDataTree(), this.loadCompanyTexts()]);
      this.transformTextTree();
    },
    async loadElements() {
      this.elements = (await BaseDataService.getElements()).data.elements;
    },
    async loadBaseDataTree() {
      this.baseDataTree = (await BaseDataService.getBaseDataTree()).data.nodes;
    },
    async loadCompanyTexts() {
      this.companyTexts = (await CompanyService.getTexts()).data;
    },
    async refreshCompanyTextTree() {
      await this.loadCompanyTexts();
      this.transformTextTree();
    },
    transformTextTree() {
      const nodesWithItems = nodesToItems(this.baseDataTree);

      this.companyTextTree = treeTransform(nodesWithItems, (item) => {
        const title = this.getTitle(item.id, item.groupId);
        const description = this.getDescription(item.id, item.groupId);

        const textChanged =
          this.companyTexts.titles[item.id] !== undefined || this.companyTexts.descriptions[item.id] !== undefined;

        return {
          title,
          description,
          textChanged,
          id: item.id,
          originalTitle: this.getTitle(item.id, item.groupId, 'standard'),
          originalDescription: this.getDescription(item.id, item.groupId, 'standard'),
          groupId: item.groupId,
          hasStandardLongtext: this.getDescription(item.id, item.groupId, 'standard') !== '',
          unit: this.getUnit(item.id),
          type: item.groupId,
          combinedText: `${title}###${description}`,
        };
      });
    },

    async changeText(options: { elementId: string; title: string | null; description: string | null }) {
      await CompanyService.changeText({
        path: { elementId: options.elementId },
        body: { title: options.title, description: options.description },
      });
      await this.refreshCompanyTextTree();
    },

    async resetAllTexts() {
      await CompanyService.resetTexts();
      await this.refreshCompanyTextTree();
    },

    async replaceTexts(search: string, replacement: string) {
      await CompanyService.replaceTexts({
        body: {
          search,
          replacement,
        },
      });
      await this.refreshCompanyTextTree();
    },

    getTitle(id: string, groupId?: GroupId, source: TextSource = 'company'): string {
      switch (groupId) {
        case 'Room':
          return 'Raum';
        case 'Floor':
          return 'Geschoss';
        case 'Element':
          return 'Element';
        default: {
          if (source === 'company') {
            const companyText = this.companyTexts.titles[id];
            if (companyText !== undefined) {
              return companyText;
            }
          }
          const element = this.elements?.[id];
          if (element === undefined) {
            return '';
          }
          return element.title;
        }
      }
    },

    getDescription(id: string, groupId?: GroupId, source: TextSource = 'company'): string {
      switch (groupId) {
        case 'Room':
          return 'Projektdaten werden nach Räumen gruppiert ausgegeben';
        case 'Floor':
          return 'Projektdaten werden nach Geschossen gruppiert ausgegeben';
        case 'Element':
          return 'Projektdaten werden nach Elementen gruppiert ausgegeben';
        default: {
          if (source === 'company') {
            const companyText = this.companyTexts.descriptions[id];
            if (companyText !== undefined) {
              return companyText;
            }
          }

          const element = this.elements?.[id];
          if (element === undefined) {
            return '';
          }
          return element.description ?? '';
        }
      }
    },

    getNumber(id: string): string {
      const element = this.elements?.[id];
      if (element === undefined) {
        return '';
      }
      return element.number ?? '';
    },

    getUnit(id: string): string {
      const element = this.elements?.[id];
      if (element === undefined) {
        return '';
      }
      return element.unit ?? '';
    },

    getConstructionModel(id: string): ConstructionModelFilter | null {
      const element = this.elements?.[id];
      if (element === undefined) {
        return null;
      }
      return element.constructionModelFilter;
    },

    getConstructionModelCount(selectedIds: string[]): ConstructionModelCount {
      const result: ConstructionModelCount = {
        common: 0,
        wood: 0,
        solid: 0,
      };

      selectedIds.forEach((id: string) => {
        const classification = this.elements[id]?.constructionModelFilter;
        if (classification === null) {
          return;
        }
        switch (classification) {
          case 'Common':
            result.common += 1;
            break;
          case 'Wood':
            result.wood += 1;
            break;
          case 'Solid':
            result.solid += 1;
            break;
        }
      });

      return result;
    },
  },
});
